import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../Styles";
import { ButtonList } from "..";
import {
  ButtonTypes,
  ListData_ValueTypes,
  SelectFieldOption_Values, TableStyles_Types,
} from "../../Definitions";
import { ReactiveTable } from "../Tables/reactiveTableRenderer";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  noop,
  useReactTable,
} from "@tanstack/react-table";
import { useEditableColumnConstructor } from "../Tables/_Tables/useEditableColumnConstructor";
import { LanControllerInfo } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLanControllersCall";
import { DynamicDataObk } from "../../Services";

import AddLcModal from "./addLcModal";
import {
  OptionsWithRequiredNumberId,
  useLcIoOptions,
} from "../../Containers/CloudScreens/GlobalScreens/Settings/_Settings/useLcIoOptions";
import { keyBy, merge } from "lodash";
import { GenericRow } from "../Tables/_Tables/useColumnConstructor";
import { valueIsSelectFieldOptionsValue } from "../../Containers/CloudScreens/GlobalScreens/Settings/_Settings/typeGuards";

interface LcTableProps {
  lanControllerInfo: LanControllerInfo[];
  maintainControllerInfo?: (
    changed?: Partial<DynamicDataObk<any>> | Partial<DynamicDataObk<any[]>>,
    deleted?: Partial<DynamicDataObk<any>> | Partial<DynamicDataObk<any[]>>,
    created?: Partial<DynamicDataObk<any>> | Partial<DynamicDataObk<any[]>>
  ) => void;
}

interface BoardData extends GenericRow {
  id: number;
  cpuId?: SelectFieldOption_Values;
  name?: string;
  system?: OptionsWithRequiredNumberId;
  index: number;
}

export default function LcTable({
  lanControllerInfo,
  maintainControllerInfo,
}: LcTableProps) {
  const { t } = useTranslation();
  const [editTable, setEditTable] = useState(false);
  const [addBoardModal, setAddBoardModal] = useState<boolean>(false);
  const { systemOptions, lanControllerCpuOptions, deviceCpuOptions } =
    useLcIoOptions();
  const dirtRef = useRef<LanControllerInfo[]>([]);
  const [boardData, setBoardData] = useState<BoardData[]>(
    [...lanControllerInfo].map((board, index) => {
      return {
        id: board.controller_id,
        cpuId: {id: board.cpu_id, label: board.cpu_id ?? ""},
        name: board.name,
        system: systemOptions.find((system) => system.id === board.system_id),
        index,
      };
    })
  );

  useEffect(
    () =>
      setBoardData(
        [...lanControllerInfo].map((board, index) => {
          return {
            id: board.controller_id,
            cpuId: {id: board.cpu_id, label: board.cpu_id ?? ""},
            name: board.name,
            system_id: systemOptions.find(
              (system) => system.id === board.system_id
            ),
            index,
          };
        })
      ),
    [lanControllerInfo, systemOptions]
  );

  const accessorData = useMemo(
    () => [
      {
        headerValue: t("devices.name", { defaultValue: "name" }),
        accessor: "name",
        type: ListData_ValueTypes.Button,
        width: "150px",
        staticColumn: true,
      },
      {
        headerValue: t("devices.cpuId", { defaultValue: "cpu id" }),
        accessor: "cpuId",
        type: ListData_ValueTypes.CreatableList,
        options: lanControllerCpuOptions,
      },
      {
        headerValue: t("devices.system", { defaultValue: "system" }),
        accessor: "system_id",
        type: ListData_ValueTypes.List,
        options: systemOptions,
      },
    ],
    [deviceCpuOptions, systemOptions, t]
  );

  const columns = useEditableColumnConstructor({
    accessorData,
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: editTable,
  });

  const table = useReactTable({
    columns,
    data: boardData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnPinning: {
        left: ["name"],
      },
    },
    meta: {
      deleteRow: noop,
      updateData: (rowIndex, columnId, value, row) => {
        const index = row.original.index;
        if (index) {
          boardData[Number(index)][columnId] = value;
          setBoardData([...boardData]);
        }
        let updateValue = value;

        if (valueIsSelectFieldOptionsValue(value, columnId)) {
          if (value.id) {
            updateValue = +value.id;
          }
        }
        if (columnId === "cpuId") {
          console.log("Select value for cpuId:", value);
          updateValue = (value as any as SelectFieldOption_Values).label;
        }
        dirtRef.current = Object.values(
          merge(
            {},
            keyBy(dirtRef.current, "lanControllerId"),
            keyBy(
              [
                {
                  lanControllerId: rowIndex,
                  [columnId]: updateValue ? updateValue : undefined,
                },
              ],
              "lanControllerId"
            )
          )
        );
      },
    },
  });
  const reactiveTable = ReactiveTable({
    table,
    tableType: TableStyles_Types.primaryTable,
    emptyText: t("devices.noControllersFound", {
      defaultValue: "no lan controllers found",
    }),
    renderFooter: false,
  });

  return (
    <>
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "grid",
          gridTemplateRows: "50px 1fr",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            gridArea: "1/1/2/2",
            backgroundColor: "transparent",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
            padding: "0px 20px",
          }}
        >
          <p
            style={{
              ...fonts.header,
              ...fonts.fontSize3,
              color: colors.greyLightTertiary,
              marginBottom: "5px",
            }}
          >
            {t("devices.lanControllers", { defaultValue: "lan controllers" })}
          </p>
          {!editTable ? (
            <ButtonList
              contents={[
                t("buttons.add", { defaultValue: "add" }),
                t("buttons.edit", { defaultValue: "edit" }),
              ]}
              onClick={(i) => {
                if (i === 0) {
                  setAddBoardModal(true);
                }
                if (i === 1) {
                  setEditTable(true);
                }
              }}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[
                glyphs.FilterParameter({}),
                glyphs.Add({}),
                glyphs.Edit({}),
              ]}
            />
          ) : (
            <ButtonList
              contents={[
                t("buttons.save", { defaultValue: "save" }),
                t("buttons.cancel", { defaultValue: "cancel" }),
                t("buttons.add", { defaultValue: "add" }),
                t("buttons.edit", { defaultValue: "edit" }),
              ]}
              onClick={(i) => {
                if (i === 0) {
                  if (maintainControllerInfo) {
                    maintainControllerInfo({ data: dirtRef.current });
                  }
                  setEditTable(false);
                }
                if (i === 1) {
                  setEditTable(false);
                  dirtRef.current = [];
                  setBoardData(
                    [...lanControllerInfo].map((board, index) => {
                      return {
                        id: board.controller_id,
                        cpuId: {id: board.cpu_id, label: board.cpu_id ?? ""},
                        name: board.name,
                        system_id: systemOptions.find(
                          (system) => system.id === board.system_id
                        ),
                        index,
                      };
                    })
                  );
                }
                if (i === 2) {
                }
                if (i === 3) {
                }
              }}
              buttonType={ButtonTypes.tableButton}
              startEnhancers={[
                glyphs.Check({}),
                glyphs.Close({}),
                glyphs.FilterParameter({}),
                glyphs.Add({}),
                glyphs.Edit({}),
              ]}
              disabled={[false, false, true, true, true]}
              overrides={(index) => ({
                baseButton: {
                  backgroundColor:
                    index === 4 ? colors.greyPrimary : colors.greySecondary,
                },
                disabledStyles: {
                  opacity: index === 4 ? 1 : 0.5,
                },
              })}
            />
          )}
        </div>
        <div
          style={{
            gridArea: "2/1/3/2",
            height: "100%",
            boxSizing: "border-box",
            position: "relative",
            backgroundColor: colors.greyLightSecondary,
            border: "5px solid " + colors.greyPrimary,
            borderRadius: "10px",
            overflow: "hidden",
          }}
        >
          {reactiveTable}
        </div>
      </div>

      {/* Add IO Modal */}
      {addBoardModal && (
        <AddLcModal
          isModalOpen={!!addBoardModal}
          closeModal={() => setAddBoardModal(false)}
          maintainControllerInfo={maintainControllerInfo}
        />
      )}
    </>
  );
}
